import Table from "@components/vendor/dashboard/table";
import { TTableColumns } from "@components/vendor/dashboard/table/types";
import { ActionIcon } from "@mantine/core";
import RoutesInstance from "@routes/instances";
import { useMutation } from "@tanstack/react-query";
import Common from "@utilities/common";
import DateHelper from "@utilities/date";
import { FC } from "react";
import { BiEdit } from "react-icons/bi";
import { IoMdRemoveCircle } from "react-icons/io";
import {
    Link,
    useLocation,
    useNavigate,
    useSearchParams,
} from "react-router-dom";
import Filters from "./filters";
import { useAuth } from "@contexts/auth";

interface Props {
    data: Array<IBrands["get"]>;
    pagination: Required<IReturnValue["pagination"]>;
    loading: boolean;
}

const BrandsContainer: FC<Props> = ({ data, loading, pagination }) => {
    const { pathname } = useLocation();

    const [URLSearchParams] = useSearchParams();

    const Navigate = useNavigate();

    const { profile } = useAuth();

    const { mutate, isLoading } = useMutation(
        ["removeBrand"],
        (id: number) =>
            Common.api.brands.Delete({
                id: id,
            }),
        {
            onSuccess: () => {
                URLSearchParams.set("refetch", "true");

                Navigate(
                    {
                        pathname,
                        search: "?" + URLSearchParams.toString(),
                    },
                    {
                        replace: true,
                    },
                );
            },
        },
    );

    const handleRemove = (id: number) => {
        mutate(id);
    };

    const columns: TTableColumns<IBrands["get"]> = [
        {
            id: "logo",
            accessorKey: "تصویر",
            cell: (data) => (
                <div className="rtl:font-numeric">
                    {data.row.original.logo ? (
                        <img
                            width={48}
                            height={48}
                            src={data.row.original.logo}
                            alt={data.row.original.faName}
                        />
                    ) : (
                        <div className="w-12">-</div>
                    )}
                </div>
            ),
        },
        {
            id: "faName",
            accessorKey: "نام فارسی",
            cell: (data) => (
                <div className="rtl:font-numeric">
                    {data.row.original.faName}
                </div>
            ),
        },
        {
            id: "enName",
            accessorKey: "نام انگلیسی",
            cell: (data) => (
                <div className="rtl:font-numeric">
                    {data.row.original.enName}
                </div>
            ),
        },
        {
            id: "createDateTime",
            accessorKey: "زمان ساخت",
            cell: (data) => (
                <div className="rtl:font-numeric">
                    {DateHelper({
                        datetime: data.row.original.createDateTime,
                        format: "DD MMMM YYYY - HH:mm",
                    })}
                </div>
            ),
        },
        {
            accessorKey: "عملیات",
            cell: (data) => (
                <div className="flex items-center flex-wrap">
                    <Link
                        to={RoutesInstance.editBrand.replace(
                            ":id",
                            data.row.original.id.toString(),
                        )}>
                        <ActionIcon
                            variant="subtle"
                            color="yellow"
                            radius={9999}
                            className="mx-1"
                            size="lg"
                            disabled={isLoading}>
                            <BiEdit className="text-2xl" />
                        </ActionIcon>
                    </Link>

                    {profile.role !== "admin" && (
                        <ActionIcon
                            variant="subtle"
                            color="red"
                            radius={9999}
                            className="mx-1"
                            size="lg"
                            loading={isLoading}
                            disabled={isLoading}
                            onClick={() => handleRemove(data.row.original.id)}>
                            <IoMdRemoveCircle className="text-2xl" />
                        </ActionIcon>
                    )}
                </div>
            ),
        },
    ];

    return (
        <>
            <Filters />

            <Table<IBrands["get"]>
                columns={columns}
                data={data}
                loading={loading}
                options={{
                    rowNumber: true,
                }}
                pagination={{
                    activePage: pagination?.page || 0,
                    isStartFromZero: false,
                    lastPage: pagination
                        ? Math.ceil(pagination?.total / pagination?.limit)
                        : 1,
                    queryKey: "page",
                }}
            />
        </>
    );
};

export default BrandsContainer;
